import { css, cx } from '@linaria/core'
import { type FC, type ReactNode, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { tmsApi } from '#api/tmsApi'
import { Button } from '#components/Button'
import { Dropdown } from '#components/Dropdown'
import { Icon } from '#components/Icon'
import { IconButton } from '#components/IconButton'
import { toastError } from '#features/ErrorHandling/toastError'
import { SystemInformationOverlayElements } from '#features/SystemInformation/SystemInformationOverlay'
import { resetSessionState, signInRedirect, signOutSilent, useLiveSessionExpiry, useUserData } from '#features/User/userManager'
import ChevronOneDownIcon from '#icons/chevron-1-down-48.svg'
import UserIcon from '#icons/user-circle-48.svg'
import { fragments, style } from '#styles'
import { flipIconClass } from '#styles/flipIcon'
import { formatDuration } from '#utils/formatDuration'

interface UserPopupProps {
  className?: string
  showUserAccountOptions?: boolean
}

const dropdownClass = css`
  display: flex;
  align-items: center;
`
const popupStyle = css`
  position: relative;
  display: flex;
  flex-direction: column;
  list-style: none;
  padding: 0;
  background-color: ${style.themeColors.backgroundPopout};
  color: ${style.themeColors.textDefault};
  outline: 0;

  box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.25);
`

const userPopupContentClass = css`
  min-width: 220px;
  display: flex;
  flex-direction: column;
  padding: 16px;
  gap: 8px;
  
  > hr {
    opacity: 0.2;
  }

  > a {
    display: flex;
    justify-content: stretch;
    flex-direction: column;
    text-decoration: none;
  }

  > div, > ._import, > ._export {
    display: flex;
    flex: 1;
    justify-content: flex-start;
    gap: 4px;
  }

  > ._import, > ._export {
    ${fragments.textStyle.body.m.regular}
  }
`

const UserPopupContent: FC<{ children?: ReactNode; showUserAccountOptions?: boolean; signedOutPath?: string }> = ({
  children,
  showUserAccountOptions = true,
  signedOutPath = '/signed-out',
}) => {
  const { t } = useTranslation('shared')
  const user = useUserData()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const expiresIn = useLiveSessionExpiry(user)

  return (
    <div className={userPopupContentClass}>
      <SystemInformationOverlayElements />

      {children && (
        <>
          <hr />
          {children}
        </>
      )}

      {showUserAccountOptions && (
        <>
          <hr />

          {user ? (
            <>
              <div>
                <strong>{t('user.name.label', 'User Name')}:</strong>
                {user.profile.name} {user.profile.given_name} {user.profile.family_name}
              </div>

              {user.profile.email && (
                <div>
                  <strong>{t('user.email.label', 'User Email')}:</strong>
                  {user.profile.email}
                </div>
              )}
            </>
          ) : (
            <div>
              <strong>{t('user.not-found', 'No user found')}</strong>
            </div>
          )}

          {expiresIn !== undefined && (
            <div>
              <strong>{t('user.token.remaining.label', 'Session Time Remaining:')}</strong>
              {formatDuration(expiresIn * 1000, undefined, { relative: false })}
            </div>
          )}

          {user && (
            <Button
              onClick={() => {
                navigate('/signed-out')

                setTimeout(() => {
                  resetSessionState()
                  dispatch(tmsApi.util.resetApiState())

                  void signOutSilent().catch(toastError)
                }, 1000)

                navigate(signedOutPath)
              }}
            >
              {t('user.button.sign-out', 'Sign Out')}
            </Button>
          )}

          {!user && (
            <Button
              onClick={() => {
                void signInRedirect().catch(toastError)
              }}
            >
              {t('user.button.sign-in', 'Sign In')}
            </Button>
          )}
        </>
      )}
    </div>
  )
}

export const UserDropdown: FC<
  UserPopupProps &
    (
      | { children?: ReactNode; isOpen?: undefined; setIsOpen?: undefined }
      | { children?: ReactNode; isOpen: boolean; setIsOpen: (value: boolean) => void }
    )
> = ({ className, showUserAccountOptions = true, children, isOpen: isOpenExternal, setIsOpen: setIsOpenExternal }) => {
  const [isOpenInternal, setIsOpenInternal] = useState(false)
  const isOpen = isOpenExternal ?? isOpenInternal
  const setIsOpen = setIsOpenExternal ?? setIsOpenInternal
  const { t } = useTranslation('shared')

  return (
    <Dropdown
      popupClassName={popupStyle}
      open={isOpen}
      onOpenChange={setIsOpen}
      dropdownContent={<UserPopupContent showUserAccountOptions={showUserAccountOptions}>{children}</UserPopupContent>}
      className={dropdownClass}
      offset={7.5}
      arrowOffset={3}
    >
      <IconButton
        iconSize="m"
        title={t('user.button.show-dropdown', 'Show User Dropdown')}
        className={cx(className, 'icon-left')}
        style={{ opacity: 1 }}
        iconUrl={UserIcon}
        onClick={(e) => {
          e.stopPropagation()
          setIsOpen(!isOpen)
        }}
      >
        <Icon iconUrl={ChevronOneDownIcon} size="xxs" className={cx(flipIconClass, isOpen && 'flipped')} />
      </IconButton>
    </Dropdown>
  )
}
